import React, { useState, useEffect } from "react";
import { HexColorPicker } from "react-colorful";
import colors from "../../colors/colors";

export interface ColorPickerProps {
  /** The current color value in 8-char RGBA hex format (e.g., #ff5500ff) */
  value: string;
  /** Called when color changes, returns 8-char RGBA hex */
  onChange: (value: string) => void;
  /** Optional className for the container */
  className?: string;
}

/**
 * Convert 8-char RGBA hex to separate hex and opacity values
 */
const hexToComponents = (rgba: string): { hex: string; opacity: number } => {
  // Normalize the input
  const normalized = rgba.startsWith("#") ? rgba : `#${rgba}`;
  const hex = normalized.slice(0, 7).padEnd(7, "0"); // #RRGGBB
  const alphaHex = normalized.slice(7, 9) || "ff";
  const alpha = parseInt(alphaHex, 16);
  const opacity = Math.round((alpha / 255) * 100);
  return { hex, opacity };
};

/**
 * Combine hex and opacity back to 8-char RGBA hex
 */
const componentsToHex = (hex: string, opacity: number): string => {
  const alpha = Math.round((opacity / 100) * 255)
    .toString(16)
    .padStart(2, "0");
  // Ensure hex is properly formatted
  const normalizedHex = hex.startsWith("#") ? hex : `#${hex}`;
  return `${normalizedHex.slice(0, 7)}${alpha}`;
};

/**
 * Validate and normalize a hex string (6-char or 8-char with alpha)
 * Returns normalized hex in format #RRGGBB or #RRGGBBAA
 */
const normalizeHex = (input: string): string | null => {
  const cleaned = input.replace(/^#/, "");
  // Match 6-char hex (#RRGGBB)
  if (/^[0-9A-Fa-f]{6}$/i.test(cleaned)) {
    return `#${cleaned.toLowerCase()}`;
  }
  // Match 8-char hex with alpha (#RRGGBBAA)
  if (/^[0-9A-Fa-f]{8}$/i.test(cleaned)) {
    return `#${cleaned.toLowerCase()}`;
  }
  return null;
};

export const ColorPicker: React.FC<ColorPickerProps> = ({
  value,
  onChange,
  className,
}) => {
  const { hex: initialHex, opacity: initialOpacity } = hexToComponents(
    value || "#000000ff"
  );

  const [hex, setHex] = useState(initialHex);
  const [opacity, setOpacity] = useState(initialOpacity);
  // Show full 8-char hex if opacity is not 100%, otherwise show 6-char
  const [hexInput, setHexInput] = useState(
    initialOpacity < 100 ? value || "#000000ff" : initialHex
  );

  // Sync internal state when value prop changes externally
  useEffect(() => {
    const { hex: newHex, opacity: newOpacity } = hexToComponents(
      value || "#000000ff"
    );
    setHex(newHex);
    setOpacity(newOpacity);
    // Show full 8-char hex if opacity is not 100%, otherwise show 6-char
    setHexInput(newOpacity < 100 ? value || "#000000ff" : newHex);
  }, [value]);

  const handleHexChange = (newHex: string) => {
    setHex(newHex);
    // Show full 8-char hex if opacity is not 100%, otherwise show 6-char
    setHexInput(opacity < 100 ? componentsToHex(newHex, opacity) : newHex);
    onChange(componentsToHex(newHex, opacity));
  };

  const handleHexInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setHexInput(inputValue);

    // Try to normalize and apply the hex
    const normalized = normalizeHex(inputValue);
    if (normalized) {
      // If it's an 8-char hex (with alpha), extract both hex and alpha
      if (normalized.length === 9) {
        const hexPart = normalized.slice(0, 7); // #RRGGBB
        const alphaHex = normalized.slice(7, 9); // AA
        const alpha = parseInt(alphaHex, 16);
        const newOpacity = Math.round((alpha / 255) * 100);
        setHex(hexPart);
        setOpacity(newOpacity);
        onChange(normalized);
      } else {
        // 6-char hex, use current opacity
        setHex(normalized);
        onChange(componentsToHex(normalized, opacity));
      }
    }
  };

  const handleHexInputBlur = () => {
    // Reset to current valid hex if input is invalid
    const normalized = normalizeHex(hexInput);
    if (!normalized) {
      // Reset to current value, showing 8-char if opacity < 100%
      const currentValue = componentsToHex(hex, opacity);
      setHexInput(opacity < 100 ? currentValue : hex);
    } else {
      // Ensure we show the full 8-char hex if it has alpha
      if (normalized.length === 9) {
        setHexInput(normalized);
      } else {
        // 6-char hex - show 8-char if opacity < 100%
        const currentValue = componentsToHex(normalized, opacity);
        setHexInput(opacity < 100 ? currentValue : normalized);
      }
    }
  };

  const handleOpacityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newOpacity = parseInt(e.target.value, 10);
    setOpacity(newOpacity);
    const newValue = componentsToHex(hex, newOpacity);
    onChange(newValue);
    // Update hex input to show 8-char if opacity < 100%, otherwise 6-char
    setHexInput(newOpacity < 100 ? newValue : hex);
  };

  return (
    <div className={`bc-color-picker ${className || ""}`}>
      <style>{`
        .bc-color-picker {
          width: 200px;
        }

        .bc-color-picker .react-colorful {
          width: 100%;
          height: auto;
        }

        .bc-color-picker .react-colorful__saturation {
          border-radius: 8px;
          height: 150px;
        }

        .bc-color-picker .react-colorful__hue {
          height: 14px;
          border-radius: 7px;
          margin-top: 16px;
        }

        .bc-color-picker .react-colorful__pointer {
          width: 20px;
          height: 20px;
        }

        .bc-color-picker-hex-input {
          width: 100%;
          margin-top: 16px;
          padding: 8px 10px;
          border: 1px solid ${colors.border_glass};
          border-radius: 8px;
          font-size: 13px;
          color: ${colors.primary};
          font-family: 'SF Mono', 'Menlo', 'Monaco', 'Courier New', monospace;
          background: ${colors.bg_light};
          outline: none;
          box-sizing: border-box;
          text-align: center;
          transition: all 0.15s ease;
        }

        .bc-color-picker-hex-input:focus {
          border-color: ${colors.blue};
          box-shadow: 0 0 0 3px rgba(85, 114, 183, 0.12);
          background: ${colors.white};
        }

        .bc-color-picker-opacity-container {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-top: 12px;
        }

        .bc-color-picker-opacity-slider {
          flex: 1;
          height: 14px;
          -webkit-appearance: none;
          appearance: none;
          background: linear-gradient(to right, white 0%, ${hex} 100%);
          border-radius: 7px;
          outline: none;
          cursor: pointer;
        }

        .bc-color-picker-opacity-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: ${colors.white};
          border: 2px solid ${colors.white};
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
          cursor: pointer;
          transition: transform 0.1s ease;
        }

        .bc-color-picker-opacity-slider::-webkit-slider-thumb:hover {
          transform: scale(1.1);
        }

        .bc-color-picker-opacity-slider::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: ${colors.white};
          border: 2px solid ${colors.white};
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
          cursor: pointer;
        }

        .bc-color-picker-opacity-value {
          min-width: 36px;
          font-size: 12px;
          font-weight: 500;
          color: ${colors.gray_medium};
          text-align: right;
          user-select: none;
        }
      `}</style>

      <HexColorPicker color={hex} onChange={handleHexChange} />

      <input
        type="text"
        value={hexInput}
        onChange={handleHexInputChange}
        onBlur={handleHexInputBlur}
        placeholder="#000000 or #000000ff"
        className="bc-color-picker-hex-input"
        maxLength={9}
      />

      <div className="bc-color-picker-opacity-container">
        <input
          type="range"
          min="0"
          max="100"
          value={opacity}
          onChange={handleOpacityChange}
          className="bc-color-picker-opacity-slider"
          style={{
            background: `linear-gradient(to right, white 0%, ${hex} 100%)`,
          }}
        />
        <span className="bc-color-picker-opacity-value">{opacity}</span>
      </div>
    </div>
  );
};
